import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useApiQuery } from '../hooks/useApiQuery';
import { useSocket } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';
import useToast from '../hooks/useToast';
import {
  CalendarIcon,
  MapPinIcon,
  DevicePhoneMobileIcon,
  UsersIcon,
  DocumentTextIcon,
  ChartBarIcon,
  QrCodeIcon,
  TicketIcon,
  CakeIcon,
  EllipsisHorizontalIcon,
  UserPlusIcon,
  WalletIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { QRCodeCanvas } from 'qrcode.react';

const PUBLIC_BASE_URL = "http://192.168.1.44:3000";

const MyEventsAdmin = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const socket = useSocket();
  const [showRegisterQRModal, setShowRegisterQRModal] = useState(false);
  const [qrEvent, setQrEvent] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);

  const isDeviceScanner = user?.role === 'device_scanner';

  // ✅ React Query: Fetch events
  const {
    data: events = [],
    isLoading,
    refetch: refetchEvents,
  } = useApiQuery(
    ['my-events'],
    () =>
      api.get('/events').then((res) => {
        const payload = res.data;
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.data)) return payload.data;
        if (Array.isArray(payload?.data?.data)) return payload.data.data;
        return [];
      }),
    {
      staleTime: 5 * 60 * 1000,
      onError: () => showError('Failed to load events'),
    },
  );

  // ✅ Socket listeners for real-time updates
  useEffect(() => {
    if (!socket) return;

    const handleRefresh = () => refetchEvents();

    socket.on('eventUserCreated', handleRefresh);
    socket.on('eventUserUpdated', handleRefresh);
    socket.on('eventUserDeleted', handleRefresh);
    socket.on('eventUsersCopied', handleRefresh);
    socket.on('eventAssignmentChanged', handleRefresh);

    return () => {
      socket.off('eventUserCreated', handleRefresh);
      socket.off('eventUserUpdated', handleRefresh);
      socket.off('eventUserDeleted', handleRefresh);
      socket.off('eventUsersCopied', handleRefresh);
      socket.off('eventAssignmentChanged', handleRefresh);
    };
  }, [socket, refetchEvents]);

  // ✅ Toggle dropdown
  const toggleDropdown = useCallback((id) => {
    setOpenDropdownId((prev) => (prev === id ? null : id));
  }, []);

  // ✅ Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openDropdownId && !e.target.closest('.dropdown-container')) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdownId]);

  // ✅ Date Formatting Helper
  const formatDateRange = (start, end) => {
    if (!start || !end) return '';
    const s = new Date(start);
    const e = new Date(end);
    const sStr = s.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    const eStr = e.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    return `${sStr} – ${eStr}`;
  };

  // ✅ Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3"></div>
          <p className="text-gray-500 text-sm">Loading events...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Events</h1>
        <p className="text-sm text-gray-500 mt-1">
          {isDeviceScanner
            ? 'Events with assigned devices'
            : 'Events assigned to you'}
        </p>
      </div>

      {/* Events Grid */}
      {events.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-12 text-center">
          <CalendarIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">
            {isDeviceScanner
              ? 'No events with assigned devices.'
              : 'No events assigned to you.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {events.map((event) => (
            <div
              key={event._id}
              className="bg-white rounded-xl border border-gray-100 shadow-sm flex flex-col relative overflow-hidden"
            >
              {/* Top-right dropdown trigger */}
              <div className="absolute top-3 right-3 z-10 dropdown-container">
                <button
                  onClick={() => toggleDropdown(event._id)}
                  className={`p-1.5 rounded-md transition-colors ${
                    openDropdownId === event._id
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                  }`}
                  aria-label="More actions"
                >
                  <EllipsisHorizontalIcon className="w-4 h-4" />
                </button>

                {openDropdownId === event._id && (
                  <div className="absolute right-0 top-8 w-48 bg-white rounded-lg border border-gray-200 shadow-lg z-[9999] py-1">
                    {/* Assign to User */}
                    <button
                      onClick={() => {
                        navigate(`/events/${event._id}/assign`);
                        setOpenDropdownId(null);
                      }}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                    >
                      <UserPlusIcon className="w-4 h-4 text-[#1672b9]" />
                      Assign to User
                    </button>

                    {/* Wallet */}
                    <button
                      onClick={() => {
                        navigate(`/events/${event._id}/wallet`);
                        setOpenDropdownId(null);
                      }}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                    >
                      <WalletIcon className="w-4 h-4 text-[#1672b9]" />
                      Wallet
                    </button>

                    {/* Divider */}
                    <div className="border-t border-gray-100 my-1"></div>

                    {/* Edit Event */}
                    <button
                      onClick={() => {
                        navigate(`/events/${event._id}/edit`);
                        setOpenDropdownId(null);
                      }}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                    >
                      <PencilIcon className="w-4 h-4 text-[#1672b9]" />
                      Edit Event
                    </button>

                    {/* Delete Event */}
                    <button
                      onClick={() => {
                        if (
                          window.confirm(
                            `Delete "${event.name}"? This cannot be undone.`,
                          )
                        ) {
                          api
                            .delete(`/events/${event._id}`)
                            .then(() => {
                              showSuccess('Event deleted');
                              refetchEvents();
                            })
                            .catch((err) =>
                              showError(
                                err.response?.data?.message ||
                                  'Failed to delete event',
                              ),
                            );
                        }
                        setOpenDropdownId(null);
                      }}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors text-left"
                    >
                      <TrashIcon className="w-4 h-4" />
                      Delete Event
                    </button>
                  </div>
                )}
              </div>

              {/* Card Content */}
              <div className="p-3 flex-1 flex flex-col">
                {/* Header */}
                <div className="flex items-center gap-1.5 mb-1.5 pr-8">
                  <h3 className="text-[13px] font-bold text-[#1672b9] tracking-tight truncate">
                    {event.name}
                  </h3>
                  <span className="inline-flex items-center gap-0.5 text-[8px] font-bold px-1.5 py-0.5 rounded-full shrink-0 bg-blue-50 text-[#1672b9]">
                    {event.eventType === 'qr' ? (
                      <QrCodeIcon className="w-2.5 h-2.5" />
                    ) : (
                      <DevicePhoneMobileIcon className="w-2.5 h-2.5" />
                    )}
                    {event.eventType === 'qr' ? 'QR' : 'NFC'}
                  </span>
                </div>

                {/* Details */}
                <div className="space-y-0.5 text-[11px] text-gray-500 mb-2">
                  <div className="flex items-center gap-1">
                    <CalendarIcon className="w-3 h-3" />
                    <span>
                      {formatDateRange(event.startDate, event.endDate)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <MapPinIcon className="w-3 h-3" />
                    <span>{event.location}</span>
                  </div>
                </div>

                {/* Stats Row */}
                <div className="grid grid-cols-2 gap-1.5 mb-2">
                  <button
                    onClick={() => navigate(`/events/${event._id}/sessions`)}
                    className="bg-blue-50 rounded-lg p-1.5 flex items-center gap-1.5 hover:bg-blue-100 transition-colors text-left"
                  >
                    <div className="p-1 bg-blue-100 rounded-md">
                      <DocumentTextIcon className="w-3.5 h-3.5 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-gray-900">
                        {event.totalScans || 0}
                      </div>
                      <div className="text-[8px] text-gray-500 font-medium">
                        Session Logs
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => navigate(`/events/${event._id}/users`)}
                    className="bg-blue-50 rounded-lg p-1.5 flex items-center gap-1.5 hover:bg-blue-100 transition-colors text-left"
                  >
                    <div className="p-1 bg-blue-100 rounded-md">
                      <UsersIcon className="w-3.5 h-3.5 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-gray-900">
                        {event.users?.length || 0}
                      </div>
                      <div className="text-[8px] text-gray-500 font-medium">
                        Users
                      </div>
                    </div>
                  </button>
                </div>

                {/* Action Grid */}
                <div className="grid grid-cols-2 gap-1.5 mb-2">
                  {/* <button
                    onClick={() =>
                      navigate(`/events/${event._id}/dashboard/overview`)
                    }
                    className="group flex items-center justify-between p-1.5 rounded-lg border border-gray-100 bg-white hover:bg-[#1471B9] transition-colors text-left"
                  >
                    <div className="flex items-center gap-1.5">
                      <ChartBarIcon className="w-3.5 h-3.5 text-[#1672b9] group-hover:text-white" />
                      <span className="text-[9px] font-semibold text-gray-700 group-hover:text-white">
                        Dashboard
                      </span>
                    </div>
                  </button> */}

                  <button
                    onClick={() => navigate(`/events/${event._id}/meals`)}
                    className="group flex items-center justify-between p-1.5 rounded-lg border border-gray-100 bg-white hover:bg-[#1471B9] transition-colors text-left"
                  >
                    <div className="flex items-center gap-1.5">
                      <CakeIcon className="w-3.5 h-3.5 text-[#1672b9] group-hover:text-white" />
                      <span className="text-[9px] font-semibold text-gray-700 group-hover:text-white">
                        Meal Coupons
                      </span>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      setQrEvent(event);
                      setShowRegisterQRModal(true);
                    }}
                    className="group flex items-center justify-between p-1.5 rounded-lg border border-gray-100 bg-white hover:bg-[#1471B9] transition-colors text-left"
                  >
                    <div className="flex items-center gap-1.5">
                      <QrCodeIcon className="w-3.5 h-3.5 text-[#1672b9] group-hover:text-white" />
                      <span className="text-[9px] font-semibold text-gray-700 group-hover:text-white">
                        Registration QR
                      </span>
                    </div>
                  </button>

                  <button
                    onClick={() => navigate(`/events/${event._id}/ticket-types`)}
                    className="group flex items-center justify-between p-1.5 rounded-lg border border-gray-100 bg-white hover:bg-[#1471B9] transition-colors text-left"
                  >
                    <div className="flex items-center gap-1.5">
                      <TicketIcon className="w-3.5 h-3.5 text-[#1672b9] group-hover:text-white" />
                      <span className="text-[9px] font-semibold text-gray-700 group-hover:text-white">
                        Ticket Types
                      </span>
                    </div>
                  </button>

                  <button
                    onClick={() => navigate(`/events/${event._id}/devices`)}
                    className="group flex items-center justify-between p-1.5 rounded-lg border border-gray-100 bg-white hover:bg-[#1471B9] transition-colors text-left"
                  >
                    <div className="flex items-center gap-1.5">
                      <DevicePhoneMobileIcon className="w-3.5 h-3.5 text-[#1672b9] group-hover:text-white" />
                      <span className="text-[9px] font-semibold text-gray-700 group-hover:text-white">
                        Devices
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Registration QR Modal */}
      {showRegisterQRModal && qrEvent && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Registration QR
              </h3>
              <button
                onClick={() => {
                  setShowRegisterQRModal(false);
                  setQrEvent(null);
                }}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                ✕
              </button>
            </div>

            <div className="text-center mb-4">
              <p className="text-sm text-gray-500 mb-1">
                Show this QR at the event entrance
              </p>
              <p className="text-xs text-gray-400">
                Attendees scan → fill form → get their personal QR
              </p>
            </div>

            <div className="bg-white p-4 rounded-lg border border-gray-200 flex items-center justify-center mb-4">
              <QRCodeCanvas
                value={`${PUBLIC_BASE_URL}/event/${qrEvent._id}/register`}
                size={240}
              />
            </div>

            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <p className="text-xs text-gray-500 mb-1">Or share this link:</p>
              <p className="text-xs font-mono text-gray-700 break-all">
                {`${PUBLIC_BASE_URL}/event/${qrEvent._id}/register`}
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(
                    `${PUBLIC_BASE_URL}/event/${qrEvent._id}/register`,
                  );
                  showSuccess('Link copied');
                }}
                className="flex-1 bg-white border border-gray-200 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-50"
              >
                Copy Link
              </button>
              <button
                onClick={() => window.print()}
                className="flex-1 bg-[#1672b9] text-white py-2 rounded-lg text-sm font-medium hover:bg-[#0f5a9e]"
              >
                Print
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyEventsAdmin;